import React, { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpenIcon, ChevronDownIcon, IdCardIcon, KeyRoundIcon, NfcIcon, PencilIcon, PrinterIcon, SparklesIcon, VolleyballIcon, WalletIcon, XIcon, BoxIcon } from "lucide-react";
import { Logo } from "../Logo";
import { CardNumber } from "./CardNumber";
import { citizen, familyMembers, walletCards, FamilyMember } from "../../data/carte";
const ease = [0.23, 1, 0.32, 1] as const;
const cardIcons: Record<string, BoxIcon> = {
  citoyenne: IdCardIcon,
  bibliotheque: BookOpenIcon,
  loisirs: VolleyballIcon
};
const iconButton = 'grid h-12 w-12 place-items-center rounded-full border border-slate-200 text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
interface DigitalCardProps {
  memberId: string;
  onEdit: (member: FamilyMember) => void;
}
export function DigitalCard({
  memberId,
  onEdit
}: DigitalCardProps) {
  const [nfcActive, setNfcActive] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [cardsOpen, setCardsOpen] = useState(false);
  const [activeId, setActiveId] = useState(walletCards[0].id);
  const holder = familyMembers.find(member => member.id === memberId) ?? familyMembers[0];
  const activeIndex = walletCards.findIndex(card => card.id === activeId);
  const active = walletCards[activeIndex];
  const others = walletCards.filter(card => card.id !== activeId);
  const ActiveIcon = cardIcons[active.id];
  const memberIndex = familyMembers.findIndex(member => member.id === holder.id);
  const directionRef = useRef(1);
  const previousMember = useRef(memberIndex);
  if (memberIndex !== previousMember.current) {
    directionRef.current = memberIndex > previousMember.current ? 1 : -1;
    previousMember.current = memberIndex;
  }
  const direction = directionRef.current;
  const selectCard = (id: string) => {
    const nextIndex = walletCards.findIndex(card => card.id === id);
    directionRef.current = nextIndex > activeIndex ? 1 : -1;
    setActiveId(id);
    setCardsOpen(false);
  };
  return <section aria-labelledby="carte-numerique-titre" className="flex flex-col" data-id="element-391" data-mp="37got8niwm7wz">
      <h2 id="carte-numerique-titre" className="sr-only" data-id="element-392" data-mp="32h2hkiiq4zqa">
        Digital card
      </h2>

      <div className="relative" data-id="element-393" data-mp="32ix5m5pal7yl">
        <AnimatePresence initial={false} mode="popLayout" custom={direction} data-id="element-394" data-mp="2xq3xd7um7hhr">
          <motion.div key={`${holder.id}-${active.id}`} custom={direction} initial={{
          x: direction * 360,
          opacity: 0,
          zIndex: 2
        }} animate={{
          x: 0,
          opacity: 1,
          zIndex: 2
        }} exit={{
          x: direction * -24,
          scale: 0.97,
          opacity: 0,
          zIndex: 1
        }} transition={{
          duration: 0.55,
          ease
        }} className="relative rounded-3xl bg-white shadow-xl shadow-ville-navy/10 ring-1 ring-slate-200" data-id="element-395" data-mp="2sqzpckjffpt0">
            <div className="relative flex items-center justify-between gap-3 rounded-t-3xl bg-ville-navy px-6 py-3.5" data-id="element-396" data-mp="z7qynzoaba3z">
              <p className="flex items-center gap-2.5 text-[17px] font-bold tracking-tight text-white" data-id="element-397" data-mp="1484ywvjn9frg">
                <ActiveIcon className="h-5 w-5" aria-hidden="true" data-id="element-398" data-mp="188oxm3jv8rfp" />
                {active.label}
              </p>

              <div className="relative" data-id="element-399" data-mp="bgv3cskmdwrz">
                <button type="button" onClick={() => setCardsOpen(v => !v)} aria-expanded={cardsOpen} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 text-sm font-bold text-white transition-colors duration-150 ease-ville hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-400" data-mp="ghizttrmb5tl">
                  +{others.length} cards
                  <motion.span animate={{
                  rotate: cardsOpen ? 180 : 0
                }} transition={{
                  duration: 0.2,
                  ease
                }} data-id="element-401" data-mp="3szfu53w3bxz3">
                    <ChevronDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-402" data-mp="34vrizspkjswd" />
                  </motion.span>
                </button>

                <AnimatePresence data-id="element-403" data-mp="3jlp94e726so7">
                  {cardsOpen && <motion.ul initial={{
                  opacity: 0,
                  y: -6,
                  scale: 0.98
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -6
                }} transition={{
                  duration: 0.18,
                  ease
                }} className="absolute right-0 top-full z-40 mt-2 w-60 overflow-hidden rounded-2xl bg-white p-1.5 shadow-xl ring-1 ring-slate-200" data-id="element-404" data-mp="3jmguyxautc1a">
                      {others.map(card => {
                    const Icon = cardIcons[card.id];
                    return <li key={card.id} data-id="element-405" data-mp="237pbj9vorf44">
                            <button type="button" onClick={() => selectCard(card.id)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-150 ease-ville hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-406" data-mp="iod60gnq5g5e">
                              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-407" data-mp="8wpi2pqdmh8o">
                                <Icon className="h-4 w-4" aria-hidden="true" data-id="element-408" data-mp="3w26rpiiqjl2d" />
                              </span>
                              <span className="min-w-0" data-id="element-409" data-mp="408kl4xaeqix">
                                <span className="block text-sm font-bold text-ville-navy" data-id="element-410" data-mp="3m5bur4241sq7">{card.label}</span>
                                <span className="block font-mono text-xs text-slate-500" data-id="element-411" data-mp="3r3n4leseo38f">{card.number}</span>
                              </span>
                            </button>
                          </li>;
                  })}
                    </motion.ul>}
                </AnimatePresence>
              </div>
            </div>

            <div className="px-7 pb-7 pt-7 text-center" data-id="element-412" data-mp="1nwec3h3168rd">
              <div className="flex items-center gap-4 text-left" data-id="element-413" data-mp="1iwtcd4g1dwdl">
                <button type="button" onClick={() => setPhotoOpen(true)} aria-label="Enlarge the photo" className="shrink-0 rounded-full transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-414" data-mp="1iymnavxon2re">
                  <img src={holder.photo} alt={`${holder.firstName} ${holder.lastName}`} className="h-20 w-20 rounded-full object-cover shadow-lg ring-4 ring-white" data-id="element-415" data-mp="26nytdmxpquph" />
                </button>
                <p className="min-w-0 flex-1 text-2xl font-extrabold leading-tight tracking-tight text-ville-navy" data-id="element-416" data-mp="1wu84wi1l6k9s">
                  {holder.firstName} {holder.lastName}
                </p>
                <button type="button" onClick={() => onEdit(holder)} aria-label={`Edit ${holder.firstName}’s card`} className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-slate-200 text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-417" data-mp="2pdx09zpryhrj">
                  <PencilIcon className="h-4 w-4" aria-hidden="true" data-id="element-418" data-mp="31xkgx8s25mdk" />
                </button>
              </div>

              <div className="mt-6 flex justify-center" data-id="element-419" data-mp="3btnj2emwtl0i">
                <Logo data-id="element-420" data-mp="3bt5e8tm6lygz" />
              </div>

              <button type="button" onClick={() => setQrOpen(true)} aria-label="Enlarge the QR code" className="mt-5 inline-block rounded-2xl border border-slate-200 p-3 transition-[transform,border-color] duration-150 ease-ville hover:-translate-y-0.5 hover:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-421" data-mp="3fur0v6oipr4d">
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=170x170&data=BONNYVILLE-${holder.id}-${active.number}`} alt={`QR code for the ${active.label} card`} className="h-36 w-36" data-id="element-422" data-mp="3ulq4e3ahxph3" />
              </button>

              <div className="mt-4" data-id="element-423" data-mp="oqdj1ugroz19">
                <CardNumber value={active.number} data-id="element-424" data-mp="jq8x3ox3bc3n" />
              </div>

              <p className="mt-4" data-id="element-425" data-mp="xp8kkt8v9ky8">
                <span className="inline-flex rounded-full bg-ville-navy px-6 py-2 text-sm font-bold text-white" data-id="element-426" data-mp="xo5z50ubcapp">
                  {active.badge}
                </span>
              </p>

              <div className="mt-7 flex items-center justify-center gap-3" data-id="element-427" data-mp="4zrz6fjnz4bi">
                <button type="button" aria-label="Print my card" className={iconButton} data-id="element-428" data-mp="z821bz3c0pd">
                  <PrinterIcon className="h-5 w-5" aria-hidden="true" data-id="element-429" data-mp="eqcthqsb1mom" />
                </button>
                <button type="button" aria-label="Add to wallet" className={iconButton} data-id="element-430" data-mp="es7d91rub8r4">
                  <WalletIcon className="h-5 w-5" aria-hidden="true" data-id="element-431" data-mp="9xjsndzyiyht" />
                </button>
                <button type="button" aria-label="Turn on NFC sharing" aria-pressed={nfcActive} onClick={() => setNfcActive(v => !v)} className={nfcActive ? 'grid h-12 w-12 place-items-center rounded-full bg-ville-indigo text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2' : iconButton} data-id="element-432" data-mp="3idd43effljs0">
                  <NfcIcon className="h-5 w-5" aria-hidden="true" data-id="element-433" data-mp="1jyffeunx3v8s" />
                </button>
                <button type="button" aria-label="Generate a one-time code" onClick={() => setOtpOpen(true)} className={iconButton} data-id="element-434" data-mp="1ow7ec8mzchmh">
                  <KeyRoundIcon className="h-5 w-5" aria-hidden="true" data-id="element-435" data-mp="2cmteldqqsxlx" />
                </button>
              </div>
              {nfcActive && <p className="mt-2 text-xs font-semibold text-ville-indigo" data-id="element-436" data-mp="2glkc3lz4eh9k">NFC on · 60 s</p>}

              <p className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500" data-id="element-437" data-mp="22q8aqvqglsjo">
                <SparklesIcon className="h-4 w-4 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-438" data-mp="27pbd28f21kny" />
                {active.note}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-4 flex justify-center gap-1" data-id="element-439" data-mp="3seen9u848gwd">
        {walletCards.map(card => <button key={card.id} type="button" onClick={() => selectCard(card.id)} aria-label={`Show the ${card.label} card`} aria-current={card.id === activeId} className="group grid h-6 place-items-center px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-440" data-mp="3sgqylojdbt0g">
            <span className={`h-2 rounded-full transition-all duration-200 ease-ville ${card.id === activeId ? 'w-6 bg-ville-indigo' : 'w-2 bg-slate-300 group-hover:bg-ville-indigo'}`} data-id="element-441" data-mp="39nwujpna9lb0" />
          </button>)}
      </div>

      <Overlay open={qrOpen} onClose={() => setQrOpen(false)} label="Enlarged QR code" data-id="element-442" data-mp="2zq8woqudrxom">
        <h3 className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-443" data-mp="34ok6j1koe86u">{active.label}</h3>
        <p className="mt-1 text-sm text-slate-600" data-id="element-444" data-mp="xym44kuubuq1">Show this code at the counter or at the door.</p>
        <img src={`https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=BONNYVILLE-${holder.id}-${active.number}`} alt="Enlarged QR code" className="mx-auto mt-5 h-60 w-60" data-id="element-445" data-mp="12x75wopqqmgl" />
        <div className="mt-4" data-id="element-446" data-mp="2nd5b0f8lmmai">
          <CardNumber value={active.number} data-id="element-447" data-mp="2ih6cvzxgxpww" />
        </div>
      </Overlay>

      <Overlay open={otpOpen} onClose={() => setOtpOpen(false)} label="One-time code" data-id="element-448" data-mp="2x5aypcrtihjw">
        <h3 className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-449" data-mp="2x749935jxlx6">One-time code</h3>
        <p className="mt-1 text-sm text-slate-600" data-id="element-450" data-mp="2scq7mj0aqik2">Generated just now, for a single use.</p>
        <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=BONNYVILLE-OTC-8241" alt="One-time QR code" className="mx-auto mt-5 h-40 w-40" data-id="element-451" data-mp="3682chj861pep" />
        <p className="mt-4 font-mono text-2xl tracking-[0.3em] text-ville-navy" data-id="element-452" data-mp="3g33yw7b24ddl">8241</p>
        <p className="mt-2 text-xs text-slate-500" data-id="element-453" data-mp="3b4ssk6jz0l04">Valid 5 minutes · single use</p>
      </Overlay>

      <Overlay open={photoOpen} onClose={() => setPhotoOpen(false)} label="Cardholder photo" data-id="element-454" data-mp="1lpcm4yxflddx">
        <img src={holder.photo} alt={`${holder.firstName} ${holder.lastName}`} className="mx-auto h-64 w-64 rounded-2xl object-cover" data-id="element-455" data-mp="1grt6pvozyuke" />
        <p className="mt-4 text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-456" data-mp="26gzxoys2czgf">
          {holder.firstName} {holder.lastName}
        </p>
        <p className="text-sm text-slate-500" data-id="element-457" data-mp="2fg4vu3zpqqs2">Town of {citizen.city}</p>
      </Overlay>
    </section>;
}
interface OverlayProps {
  open: boolean;
  onClose: () => void;
  label: string;
  children: React.ReactNode;
}
function Overlay({
  open,
  onClose,
  label,
  children
}: OverlayProps) {
  return <AnimatePresence data-id="element-458" data-mp="2tfeav0c6neug">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} data-id="element-459" data-mp="2tel25ttnyf9o">
          <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-ville-navy/40" data-id="element-460" data-mp="384iumlymlr4o" />
          <motion.div role="dialog" aria-modal="true" aria-label={label} initial={{
        y: 20,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 12,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease
      }} className="relative w-full max-w-sm rounded-t-3xl bg-white p-7 text-center shadow-2xl sm:rounded-3xl" data-id="element-461" data-mp="3d3dkg7v0gfya">
            <button type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-462" data-mp="mkaydyqdw97o">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-463" data-mp="1af58e99i8xab" />
            </button>
            {children}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}